import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const splitSpecification = (value = "") => {
  const [itemName, ...descriptionParts] = String(value).split(" - ");
  return { itemName, description: descriptionParts.join(" - ") };
};

const joinSpecification = (itemName = "", description = "") => {
  // Keep the user's in-progress item name exactly as typed. Trimming here
  // removed a trailing space on every keypress, making multi-word names hard
  // to enter manually.
  const name = String(itemName);
  const details = String(description).trim();
  return details ? `${name} - ${details}` : name;
};

const extractSpecificationUom = (value = "", fallbackUom = "Nos") => {
  const match = String(value).match(/(?:\s*[—-]\s*|\u2063)UOM\s*:\s*([^—-]+)\s*$/i);
  return {
    name: String(value).replace(/(?:\s*[—-]\s*|\u2063)UOM\s*:\s*[^—-]+\s*$/i, "").trim(),
    uom: match?.[1]?.trim() || fallbackUom || "Nos"
  };
};

const emptyPaymentStage = (index = 0) => ({
  stage: `Stage ${index + 1}`,
  percentage: "",
  term: ""
});

const normalizePaymentStages = (stages) => {
  if (Array.isArray(stages)) return stages;
  if (typeof stages === "string") {
    try {
      const parsed = JSON.parse(stages);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
};

const normalizeAdditionalTerms = (terms) => {
  if (typeof terms === "string") {
    try {
      terms = JSON.parse(terms);
    } catch (_) {
      terms = [];
    }
  }
  return Array.isArray(terms) ? terms.map((term) => String(term || "")) : [];
};

const toDateInputValue = (value) => value ? String(value).slice(0, 10) : "";

function POPage() {
  const [form, setForm] = useState({
    order_id: "",
    date: "",
    vendor_id: "",
    vendor_search: "",
    site_id: "",
    site_search: "",
    delivery_address_line1: "",
    delivery_address_line2: "",
    delivery_address_line3: "",
    payment_term: "",
    delivery_date: "",
    delivery_contact: "",
    delivery_phone: "",
    creator_name: "",
    creator_mobile: "",
    payment_type: "Advance Payment",
    advance_percent: 0,
    credit_days: "30",
    running_basis: "Work Progress",
    payment_stages: [],
    tax_mode: "CGST_SGST",
    tds_percent: 0,
    overall_discount_amount: "",
    comparison_id: "",
    approval_status: "Pending",
    transportation: "Exclusive",
    unloading: "Exclusive",
    additional_terms: [],
  });

  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [masterPrices, setMasterPrices] = useState([]);
  const [suggestions, setSuggestions] = useState({});

  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const [poStatus, setPoStatus] = useState("Draft");
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const savingDraftRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};


useEffect(() => {
  const user = JSON.parse(
    localStorage.getItem("user")
  );

  if (!user) return;

  setForm((prev) => ({
    ...prev,
    creator_name: user.name || "",
    creator_mobile: user.phone_number || ""
  }));
}, []);
  const [items, setItems] = useState([
    { name: "", qty: 1, uom: "Nos", price: 0, basePrice: 0 }
  ]);

  // Dynamic Base URL Auto-Fallback Resolver
  const getApiUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    return window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://ensileta-erp-backend-production.up.railway.app";
  };

  useEffect(() => {
     const API_BASE = getApiUrl();

if (state.draftId) {
  authFetch(`${API_BASE}/po/${state.draftId}`)
    .then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Unable to fetch PO details");
      return data;
    })
    .then((po) => {
      setPoStatus(po.status || "Draft");

      setForm((prev) => ({
        ...prev,
        order_id: po.order_id,
        date: po.date?.split("T")[0] || "",
        vendor_id: po.vendor_id,
        site_id: po.site_id,

        delivery_address_line1: po.delivery_address_line1 || "",
        delivery_address_line2: po.delivery_address_line2 || "",
        delivery_address_line3: po.delivery_address_line3 || "",
        payment_term: po.payment_term || "",

        delivery_contact: po.delivery_contact || "",
        delivery_phone: po.delivery_phone || "",

        creator_name: po.creator_name || "",
        creator_mobile: po.creator_mobile || "",

        payment_type: po.payment_type || "Advance Payment",
        advance_percent: po.advance_percent || 0,
        credit_days: po.credit_days || "30",
        running_basis: po.running_basis || "Work Progress",
        payment_stages: normalizePaymentStages(po.payment_stages),
        tax_mode: po.tax_mode || "CGST_SGST",
        tds_percent: po.tds_percent || 0,
        overall_discount_amount: Number(po.overall_discount_amount) || "",
        comparison_id: po.comparison_id || "",
        approval_status: po.approval_status || "Pending",
        transportation: po.transportation || "Exclusive",
        unloading: po.unloading || "Exclusive",
        additional_terms: normalizeAdditionalTerms(po.additional_terms),

        // PostgreSQL can return DATE values with a time portion; HTML date
        // inputs require YYYY-MM-DD, otherwise the saved value appears blank.
        delivery_date: toDateInputValue(po.delivery_date)
      }));

      if (po.items) {
       setItems(
  po.items.map((item) => ({
    name: item.name,
    qty: item.qty,
    uom: item.uom || "Nos",
    price: Number(item.rate),
    basePrice: Number(item.rate),
    gst_percent: Number(item.gst_percent) || 0
  }))
);
      }
    })
    .catch((err) => console.error("PO edit data fetch failed:", err));
}

    authFetch(`${API_BASE}/vendors`)
      .then((res) => res.json())
      .then((data) => setVendors(data))
      .catch((err) => console.error("Vendor fetch breakdown:", err));

    authFetch(`${API_BASE}/sites`)
      .then((res) => res.json())
      .then((data) => setSites(data))
      .catch((err) => console.error("Site fetch breakdown:", err));

    authFetch(`${API_BASE}/price-list`)
      .then((res) => res.json())
      .then((data) => setMasterPrices(data))
      .catch((err) => console.error("Price collection sync block:", err));

    // Do not overwrite values loaded from an existing PO.
    if (!state.draftId) {
      setForm((prev) => ({
        ...prev,
        date: new Date().toISOString().split("T")[0],
        vendor_id: state.vendor_id || "",
        site_id: state.site_id || "",
        comparison_id: state.comparison_id || "",
        approval_status: state.approval_status || "Pending",
        payment_type: state.payment_type || prev.payment_type,
        payment_stages: normalizePaymentStages(state.payment_stages)
      }));

      if (state.items && state.items.length > 0) {
        setItems(
          state.items.map((item) => {
            const specification = extractSpecificationUom(item.name, item.uom);
            return {
              name: specification.name,
              qty: item.qty || 1,
              uom: specification.uom,
              price: item.price || 0,
              basePrice: item.price || 0,
              gst_percent: Number(item.gst_percent) || 0
            };
          })
        );
      }
    }
  }, [state.draftId, state.vendor_id, state.site_id, state.items, state.comparison_id, state.approval_status]);

 

  // Sync details when explicit vendor selections match keys
  useEffect(() => {
    if (form.vendor_id && vendors.length > 0) {
      const match = vendors.find((v) => String(v.id) === String(form.vendor_id));
      if (match && form.vendor_search !== match.name) {
        setForm((p) => ({ ...p, vendor_search: match.name }));
      }
    }
  }, [form.vendor_id, vendors]);

  // Sync details when site changes
 useEffect(() => {
  if (!form.site_id || sites.length === 0) return;

 const selectedSite = sites.find(
    (s) => String(s.id) === String(form.site_id)
  );

  if (!selectedSite) return;

  // A saved PO owns its number, address, and contact values. Reopening it must
  // only restore the display label, never regenerate or overwrite those values.
  if (state.draftId) {
    setForm((prev) => prev.site_search === selectedSite.site_name
      ? prev
      : { ...prev, site_search: selectedSite.site_name });
    return;
  }

  authFetch(`${getApiUrl()}/next-po-number/${form.site_id}`)
  .then((res) => res.json())
  .then((data) => {
    setForm((prev) => ({
      ...prev,
      order_id: data.orderId
    }));
  })
  .catch((err) =>
    console.error("PO Number Error:", err)
  );

  setForm((prev) => ({
    ...prev,
    site_search: selectedSite.site_name,
    delivery_address_line1: selectedSite.address || "",
    delivery_address_line2: selectedSite.city || "",
    delivery_address_line3: ""
  }));

  authFetch(`${getApiUrl()}/project-team/${form.site_id}`)
    .then((res) => res.json())
    .then((team) => {
      if (!team || team.message) return;

      setForm((prev) => ({
        ...prev,

        // Don't overwrite manual entries
        delivery_contact:
          prev.delivery_contact ||
          team.supervisor ||
          "",

        delivery_phone:
          prev.delivery_phone ||
          team.supervisor_mobile ||
          ""
      }));
    });

    setForm((prev) => ({
    ...prev,
    site_search: selectedSite.site_name,
    delivery_address_line1: selectedSite.address || "",
    delivery_address_line2: selectedSite.city || "",
    delivery_address_line3: ""
  }));

  authFetch(`${getApiUrl()}/project-team/${form.site_id}`)
    .then((res) => res.json())
    .then((team) => {
      if (!team || team.message) return;

      setForm((prev) => ({
        ...prev,
        delivery_contact:
          prev.delivery_contact ||
          team.supervisor ||
          "",

        delivery_phone:
          prev.delivery_phone ||
          team.supervisor_mobile ||
          ""
      }));
    })
    .catch((err) =>
      console.error("Project Team Fetch Error:", err)
    );

}, [sites, form.site_id, state.draftId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const paymentStageTotal = form.payment_stages.reduce(
    (total, stage) => total + (Number(stage.percentage) || 0),
    0
  );

  const updatePaymentStage = (index, field, value) => {
    setForm((current) => ({
      ...current,
      payment_stages: current.payment_stages.map((stage, stageIndex) =>
        stageIndex === index ? { ...stage, [field]: value } : stage
      )
    }));
  };

  const addPaymentStage = () => {
    setForm((current) => ({
      ...current,
      payment_stages: [...current.payment_stages, emptyPaymentStage(current.payment_stages.length)]
    }));
  };

  const deletePaymentStage = (index) => {
    setForm((current) => ({
      ...current,
      payment_stages: current.payment_stages.filter((_, stageIndex) => stageIndex !== index)
    }));
  };

  const addAdditionalTerm = () => {
    setForm((current) => ({
      ...current,
      additional_terms: [...current.additional_terms, ""]
    }));
  };

  const updateAdditionalTerm = (index, value) => {
    setForm((current) => ({
      ...current,
      additional_terms: current.additional_terms.map((term, termIndex) =>
        termIndex === index ? value : term
      )
    }));
  };

  const removeAdditionalTerm = (index) => {
    setForm((current) => ({
      ...current,
      additional_terms: current.additional_terms.filter((_, termIndex) => termIndex !== index)
    }));
  };

  const movePaymentStage = (index, direction) => {
    setForm((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.payment_stages.length) return current;
      const stages = [...current.payment_stages];
      [stages[index], stages[target]] = [stages[target], stages[index]];
      return { ...current, payment_stages: stages };
    });
  };

  const handleItemChange = (index, field, value) => {
    setItems((current) => current.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    ));
  };

  const handleMaterialSearch = (value, index) => {
    // Keep the saved specification intact, but let the item-name field show
    // only the item name; the description is edited in its own field below.
    setItems((current) => current.map((item, itemIndex) => itemIndex === index
      ? { ...item, name: joinSpecification(value, splitSpecification(item.name).description), price: 0, basePrice: 0 }
      : item
    ));

    if (!value.trim()) {
      setSuggestions((prev) => ({ ...prev, [index]: [] }));
      return;
    }

    const searchWords = value
      .toLowerCase()
      .replace(/mm/g, "")
      .split(/[\s-]+/)
      .filter(Boolean);
    const matches = masterPrices.filter((priceCard) => {
      if (
        form.vendor_id &&
        String(priceCard.vendor_id) !== String(form.vendor_id)
      ) {
        return false;
      }
      const nameLower = (priceCard.item_name || "").toLowerCase();
      return searchWords.every((word) => nameLower.includes(word));
    });

    setSuggestions((prev) => ({ ...prev, [index]: matches.slice(0, 8) }));
  };

  const filteredVendors = vendors.filter((v) =>
    (v.name || "").toLowerCase().includes(form.vendor_search.toLowerCase())
  );

  const filteredSites = sites.filter((s) =>
    (s.site_name || "").toLowerCase().includes(form.site_search.toLowerCase())
  );

  const addItem = () =>
    setItems([...items, { name: "", qty: 1, uom: "Nos", price: 0, basePrice: 0, gst_percent: 0 }]);
  const removeItem = (index) =>
    items.length > 1 && setItems(items.filter((_, i) => i !== index));
  const baseAmount = items.reduce((sum, item) => sum + (Number(item.qty) || 0) * (Number(item.price) || 0), 0);
  const discountAmount = Math.min(Math.max(Number(form.overall_discount_amount) || 0, 0), baseAmount);
  const subtotalAmount = baseAmount - discountAmount;
  const gstAmount = items.reduce((sum, item) => {
    const lineAmount = (Number(item.qty) || 0) * (Number(item.price) || 0);
    return sum + lineAmount * (Number(item.gst_percent) || 0) / 100;
  }, 0) * (baseAmount ? subtotalAmount / baseAmount : 0);
  const tdsAmount = subtotalAmount * (Number(form.tds_percent) || 0) / 100;
  const cgstAmount = form.tax_mode === "IGST" ? 0 : gstAmount / 2;
  const sgstAmount = form.tax_mode === "IGST" ? 0 : gstAmount / 2;
  const igstAmount = form.tax_mode === "IGST" ? gstAmount : 0;
  const totalAmount = subtotalAmount + gstAmount - tdsAmount;

  const saveDraft = async () => {
  if (savingDraftRef.current) return;
  savingDraftRef.current = true;
  setIsSavingDraft(true);
  try {
    const API_BASE = getApiUrl();

    console.log("SAVE DRAFT DATA", {
  creator_name: form.creator_name,
  creator_mobile: form.creator_mobile
});

    const response = await authFetch(
      state.draftId ? `${API_BASE}/po/${state.draftId}` : `${API_BASE}/create-po`,
      {
        method: state.draftId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...form,
          status: state.draftId ? poStatus : "Draft",
          total_amount: totalAmount,
          items
        })
      }
    );

    const data = await response.json();

    if (!response.ok || !data.success) throw new Error(data.error || "Unable to save draft");
    alert("Draft Saved Successfully");
    navigate("/purchase-order", { state: { draftId: data.po_id || state.draftId } });
  } catch (err) {
    console.error(err);
    alert("Failed to save draft");
  } finally {
    savingDraftRef.current = false;
    setIsSavingDraft(false);
  }
};

  const handleSubmit = async () => {
    if (!state.draftId) {
      alert("Please click Save Draft before opening Preview PO. Preview uses the saved PO ID.");
      return;
    }
    if (!form.vendor_id || !form.site_id) {
      alert("Please select a Vendor and Site before loading Preview layout context.");
      return;
    }

    const total = Math.round(totalAmount);

    const chosenVendor = vendors.find(
      (v) => String(v.id) === String(form.vendor_id)
    );
    const chosenSite = sites.find((s) => String(s.id) === String(form.site_id));

    const draftPayload = {
  order_id: form.order_id,
  date: form.date,
  vendor_id: form.vendor_id,
  site_id: form.site_id,
  total_amount: total,
  tax_mode: form.tax_mode,
  tds_percent: form.tds_percent,
  overall_discount_amount: discountAmount,
  base_amount: baseAmount,
  subtotal_amount: subtotalAmount,
  cgst_amount: cgstAmount,
  sgst_amount: sgstAmount,
  igst_amount: igstAmount,
  tds_amount: tdsAmount,
  comparison_id: state.comparison_id || "",
  approval_status: state.approval_status || "Pending",

  delivery_address_line1: form.delivery_address_line1,
  delivery_address_line2: form.delivery_address_line2,
  delivery_address_line3: form.delivery_address_line3,
  delivery_contact: form.delivery_contact,
  delivery_phone: form.delivery_phone,

  creator_name: form.creator_name,
  creator_mobile: form.creator_mobile,

  status: poStatus,

  vendor_name: chosenVendor ? chosenVendor.name : "",

  // ADD THESE ↓↓↓
  vendor_address_line1: chosenVendor?.address_line1 || "",
  vendor_address_line2: chosenVendor?.address_line2 || "",
  vendor_address_line3: chosenVendor?.address_line3 || "",
  gst_no: chosenVendor?.gst_no || "",
  contact_person: chosenVendor?.contact_person || "",
  contact_number: chosenVendor?.contact_number || "",
  // ADD THESE ↑↑↑

  site_name: chosenSite ? chosenSite.site_name : "",

  payment_type: form.payment_type,
  advance_percent: form.advance_percent,
  credit_days: form.credit_days,
  running_basis: form.running_basis,
  payment_stages: form.payment_stages,
  delivery_date: form.delivery_date,
  transportation: form.transportation,
  unloading: form.unloading,
  additional_terms: normalizeAdditionalTerms(form.additional_terms).map((term) => term.trim()).filter(Boolean),
  items: items.map((item) => ({
    name: item.name,
    qty: Number(item.qty),
    uom: item.uom || "",
    rate: Number(item.price),
    gst_percent: Number(item.gst_percent) || 0
  }))
};

    try {
      const saveResponse = await authFetch(`${getApiUrl()}/po/${state.draftId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, status: poStatus, total_amount: totalAmount, items })
      });
      const saveResult = await saveResponse.json();
      if (!saveResponse.ok || !saveResult.success) throw new Error(saveResult.error || "Unable to save PO before preview");
      navigate(`/print/${state.draftId}`, { state: { draftData: { ...draftPayload, id: state.draftId }, isDraft: true } });
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div className="po-page" style={{ background: "#f8fafc", minHeight: "100vh", width: "100%", padding: "24px" }}>
      <div className="container">
        <div className="card shadow-lg border-0 mb-4">
          <div className="card-body">
            <h4 className="mb-4 fw-bold text-primary">Create Purchase Order</h4>

            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label fw-semibold">Order ID</label>
                <input className="form-control" name="order_id" value={form.order_id} onChange={handleChange} />
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Date</label>
                <input type="date" className="form-control" name="date" value={form.date} onChange={handleChange} />
              </div>

              {/* SEARCHABLE VENDOR DROPDOWN */}
              <div className="col-md-3" style={{ position: "relative" }}>
                <label className="form-label fw-semibold">Vendor</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type to search vendor..."
                  value={form.vendor_search}
                  onFocus={() => setShowVendorDropdown(true)}
                  onBlur={() => setTimeout(() => setShowVendorDropdown(false), 250)}
                  onChange={(e) => setForm({ ...form, vendor_search: e.target.value, vendor_id: "" })}
                />
                {showVendorDropdown && (
                  <div style={{ position: "absolute", zIndex: 100, background: "#fff", width: "93%", maxHeight: "200px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "4px", boxShadow: "0px 4px 6px rgba(0,0,0,0.1)" }}>
                    {filteredVendors.length === 0 ? (
                      <div style={{ padding: "8px", color: "#888" }}>No vendors found</div>
                    ) : (
                      filteredVendors.map((v) => (
                        <div
                          key={v.id}
                          style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #f9f9f9" }}
                          onMouseDown={() => setForm({ ...form, vendor_id: v.id, vendor_search: v.name })}
                          onMouseEnter={(e) => (e.target.style.background = "#f1f5f9")}
                          onMouseLeave={(e) => (e.target.style.background = "transparent")}
                        >
                          {v.name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* SEARCHABLE SITE DROPDOWN */}
              <div className="col-md-3" style={{ position: "relative" }}>
                <label className="form-label fw-semibold">Site</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type to search site..."
                  value={form.site_search}
                  onFocus={() => setShowSiteDropdown(true)}
                  onBlur={() => setTimeout(() => setShowSiteDropdown(false), 250)}
                  onChange={(e) => setForm({ ...form, site_search: e.target.value, site_id: "" })}
                />
                {showSiteDropdown && (
                  <div style={{ position: "absolute", zIndex: 100, background: "#fff", width: "93%", maxHeight: "200px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "4px", boxShadow: "0px 4px 6px rgba(0,0,0,0.1)" }}>
                    {filteredSites.length === 0 ? (
                      <div style={{ padding: "8px", color: "#888" }}>No sites found</div>
                    ) : (
                      filteredSites.map((s) => (
                        <div
                          key={s.id}
                          style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #f9f9f9" }}
                          onMouseDown={() => setForm({ ...form, site_id: s.id, site_search: s.site_name })}
                          onMouseEnter={(e) => (e.target.style.background = "#f1f5f9")}
                          onMouseLeave={(e) => (e.target.style.background = "transparent")}
                        >
                          {s.site_name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <hr className="my-4" />
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fw-bold text-secondary">PO Creator Name</label>
                <input type="text" className="form-control" name="creator_name" placeholder="Enter full name" value={form.creator_name} readOnly />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-bold text-secondary">PO Creator Mobile</label>
                <input type="text" className="form-control" name="creator_mobile" placeholder="Enter contact number" value={form.creator_mobile} readOnly />
              </div>
            </div>

            <hr className="my-4" />
            <h6 className="fw-bold mb-3 text-secondary">Delivery Details</h6>
            <div className="row g-3">
              <div className="col-md-4"><input className="form-control" placeholder="Address Line 1" value={form.delivery_address_line1} onChange={(e) => setForm({ ...form, delivery_address_line1: e.target.value })} /></div>
              <div className="col-md-4"><input className="form-control" placeholder="Address Line 2" value={form.delivery_address_line2} onChange={(e) => setForm({ ...form, delivery_address_line2: e.target.value })} /></div>
              <div className="col-md-4"><input className="form-control" placeholder="Address Line 3" value={form.delivery_address_line3} onChange={(e) => setForm({ ...form, delivery_address_line3: e.target.value })} /></div>
              <div className="col-md-6"><input className="form-control" placeholder="Contact Person" value={form.delivery_contact} onChange={(e) => setForm({ ...form, delivery_contact: e.target.value })} /></div>
              <div className="col-md-6"><input className="form-control" placeholder="Phone Number" value={form.delivery_phone} onChange={(e) => setForm({ ...form, delivery_phone: e.target.value })} /></div>
            </div>

            <div className="row g-3 mt-2">
              <div className="col-md-3">
                <label className="form-label fw-semibold">Transportation</label>
                <select className="form-control" name="transportation" value={form.transportation} onChange={handleChange}>
                  <option value="Inclusive">Inclusive</option>
                  <option value="Exclusive">Exclusive</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Unloading</label>
                <select className="form-control" name="unloading" value={form.unloading} onChange={handleChange}>
                  <option value="Inclusive">Inclusive</option>
                  <option value="Exclusive">Exclusive</option>
                </select>
              </div>
            </div>

            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
  Payment Type
</label>

<select
  className="form-control"
  name="payment_type"
  value={form.payment_type}
  onChange={handleChange}
>

  <option value="Full Payment">
  Full Payment
  </option>
  <option value="Advance Payment">
    Advance Payment
  </option>

  <option value="Credit Payment">
    Credit Payment
  </option>

  <option value="Running Payment">
    Running Payment
  </option>
</select>
{["Advance Payment", "Running Payment"].includes(form.payment_type) && (
  <div className="mt-3">
    <div className="d-flex justify-content-between align-items-center mb-2">
      <label className="fw-semibold">Payment Stages</label>
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={addPaymentStage}>+ Add Stage</button>
    </div>
    <div className="table-responsive">
      <table className="table table-sm table-bordered align-middle mb-2">
        <thead className="table-light">
          <tr><th>Stage</th><th style={{ width: "130px" }}>Percentage</th><th>Payment Term</th><th style={{ width: "190px" }}>Actions</th></tr>
        </thead>
        <tbody>
          {form.payment_stages.map((stage, index) => (
            <tr key={`payment-stage-${index}`}>
              <td><input className="form-control form-control-sm" value={stage.stage || ""} onChange={(e) => updatePaymentStage(index, "stage", e.target.value)} /></td>
              <td><input type="number" min="0" max="100" step="0.01" className="form-control form-control-sm" value={stage.percentage} onChange={(e) => updatePaymentStage(index, "percentage", e.target.value)} /></td>
              <td><input className="form-control form-control-sm" value={stage.term || ""} onChange={(e) => updatePaymentStage(index, "term", e.target.value)} /></td>
              <td>
                <button type="button" className="btn btn-sm btn-light me-1" onClick={() => movePaymentStage(index, -1)} disabled={index === 0}>↑</button>
                <button type="button" className="btn btn-sm btn-light me-1" onClick={() => movePaymentStage(index, 1)} disabled={index === form.payment_stages.length - 1}>↓</button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deletePaymentStage(index)}>Delete</button>
              </td>
            </tr>
          ))}
          {!form.payment_stages.length && <tr><td colSpan="4" className="text-muted text-center">Add payment stages manually.</td></tr>}
        </tbody>
        <tfoot>
          <tr><th colSpan="1">Total</th><th className={Math.abs(paymentStageTotal - 100) < 0.001 ? "text-success" : "text-danger"}>{paymentStageTotal}%</th><th colSpan="2">{Math.abs(paymentStageTotal - 100) < 0.001 ? "Total = 100%" : "Total must be 100% before approval"}</th></tr>
        </tfoot>
      </table>
    </div>
  </div>
)}
{form.payment_type === "Credit Payment" && (
  <div className="mt-2">
    <label>Credit Days</label>
    <input
      type="text"
      className="form-control"
      name="credit_days"
      value={form.credit_days}
      onChange={handleChange}
      placeholder="e.g. 30 days after invoice"
    />
  </div>
)}
{form.payment_type === "Full Payment" && (
  <div className="mt-2">
    <label>Payment Terms</label>

    <input
      className="form-control"
      value="100% Full Payment"
      readOnly
    />
  </div>
)}
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Delivery Date</label>
                <input type="date" className="form-control" name="delivery_date" value={form.delivery_date} onChange={handleChange} />
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-lg border-0 mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
              <div>
                <h6 className="fw-bold mb-1 text-secondary">Additional Terms &amp; Conditions</h6>
                <p className="small text-muted mb-0">The standard PO terms remain included by default. Add only PO-specific terms below when needed.</p>
              </div>
              <button type="button" className="btn btn-sm btn-outline-primary flex-shrink-0" onClick={addAdditionalTerm}>+ Add Term</button>
            </div>

            {form.additional_terms.length > 0 ? (
              <div className="d-grid gap-2 mt-3">
                {form.additional_terms.map((term, index) => (
                  <div className="d-flex align-items-start gap-2" key={`additional-term-${index}`}>
                    <span className="pt-2 text-muted fw-semibold">{index + 1}.</span>
                    <textarea
                      className="form-control"
                      rows="2"
                      value={term}
                      onChange={(event) => updateAdditionalTerm(index, event.target.value)}
                      placeholder="Enter an additional term or condition"
                      aria-label={`Additional term ${index + 1}`}
                    />
                    <button type="button" className="btn btn-outline-danger" onClick={() => removeAdditionalTerm(index)} aria-label={`Remove additional term ${index + 1}`}>Remove</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="small text-muted mb-0 mt-3">No additional terms added.</p>
            )}
          </div>
        </div>

        <div className="card shadow-lg border-0 mb-4">
          <div className="card-body">
            <h6 className="fw-bold mb-3 text-secondary">Tax & Deduction</h6>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">GST Calculation</label>
                <select className="form-control" name="tax_mode" value={form.tax_mode} onChange={handleChange}>
                  <option value="CGST_SGST">CGST + SGST</option>
                  <option value="IGST">IGST</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">TDS %</label>
                <select className="form-control" name="tds_percent" value={form.tds_percent} onChange={handleChange}>
                  <option value="0">0%</option><option value="0.1">0.1%</option><option value="1">1%</option><option value="2">2%</option><option value="10">10%</option>
                </select>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <div className="small text-muted">GST: ₹{gstAmount.toFixed(2)} &nbsp; TDS: ₹{tdsAmount.toFixed(2)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ITEMS SELECTION BLOCK TABLE */}
        <div className="card shadow-lg border-0 mb-4" style={{ overflow: "visible" }}>
          <div className="card-body" style={{ overflow: "visible" }}>
            <h5 className="fw-bold mb-3">Items</h5>
            <div className="table-responsive" style={{ overflow: "visible" }}>
              <table className="po-items-table table table-hover align-middle" style={{ overflow: "visible" }}>
                <thead className="table-light">
                  <tr>
                    <th>Item Name</th>
                    <th width="90">UOM</th>
                    <th width="100">Qty</th>
                    <th width="150">Price</th>
                    <th width="110">GST %</th>
                    <th width="150">Total</th>
                    <th width="60" className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody style={{ overflow: "visible" }}>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td style={{ position: "relative" }}>
                        <div style={{ minWidth: 0 }}>
                        <input
                          className="form-control"
                          value={splitSpecification(item.name).itemName}
                          placeholder="Item name"
                          id={`po-input-row-${index}`}
                          onChange={(e) => {
                            handleMaterialSearch(e.target.value, index);
                          }}
                          onBlur={() => setTimeout(() => setSuggestions((prev) => ({ ...prev, [index]: [] })), 200)}
                          autoComplete="off"
                          style={{ minHeight: "30px", padding: "5px 8px", fontSize: "13px", fontWeight: 600, borderRadius: "6px 6px 0 0" }}
                        />
                        <textarea rows={1} value={splitSpecification(item.name).description} placeholder="Description" onChange={(e) => { const itemName = splitSpecification(item.name).itemName; handleItemChange(index, "name", joinSpecification(itemName, e.target.value)); e.currentTarget.style.height = "auto"; e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`; }} onInput={(e) => { e.currentTarget.style.height = "auto"; e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`; }} className="form-control" style={{ minHeight: "24px", padding: "4px 8px", fontSize: "11px", lineHeight: "16px", color: "#64748b", resize: "none", overflow: "hidden", borderTop: "none", borderRadius: "0 0 6px 6px" }} />
                        </div>
                        {suggestions[index]?.length > 0 && (
                          <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", background: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "8px", overflow: "hidden", zIndex: 9999, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)" }}>
                            {suggestions[index].map((priceCard, i) => (
                              <div
                                key={i}
                                onClick={() => {
                                  const catalogPrice = Number(priceCard.rate || 0);
                                  const updated = [...items];
                                  updated[index] = {
                                    ...updated[index],
                                    name: priceCard.item_name,
                                    price: catalogPrice,
                                    basePrice: catalogPrice
                                  };
                                  setItems(updated);
                                  setSuggestions((prev) => ({ ...prev, [index]: [] }));
                                }}
                                style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                              >
                                <div style={{ fontWeight: "600", fontSize: "13px", color: "#0f172a" }}>{priceCard.item_name}</div>
                                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                                  {priceCard.vendor_name ? `Vendor: ${priceCard.vendor_name} ` : ""} • Rate: ₹{priceCard.rate || 0}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td><input className="form-control" value={item.uom || ""} onChange={(e) => handleItemChange(index, "uom", e.target.value)} /></td>
                      <td>
                        <input type="number" className="form-control" value={item.qty} min="1" onChange={(e) => handleItemChange(index, "qty", +e.target.value)} />
                      </td>
                      <td>
                        <input type="number" className="form-control" value={item.price} onChange={(e) => handleItemChange(index, "price", Number(e.target.value))} />
                      </td>
                      <td>
                        <select className="form-control" value={item.gst_percent ?? 0} onChange={(e) => handleItemChange(index, "gst_percent", Number(e.target.value))}>
                          <option value="0">0%</option><option value="5">5%</option><option value="12">12%</option><option value="18">18%</option><option value="28">28%</option>
                        </select>
                      </td>
                      <td className="fw-semibold text-success">₹ {item.qty * item.price}</td>
                      <td className="text-center">
                        {items.length > 1 && <button className="btn btn-sm btn-outline-danger" onClick={() => removeItem(index)}>✕</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <button className="btn btn-outline-primary fw-semibold" onClick={addItem}>+ Add Row Item</button>
              <div className="d-flex align-items-end gap-3">
                <div>
                  <label className="form-label fw-semibold mb-1">Overall Discount Amount <span className="text-muted fw-normal">(Optional)</span></label>
                  <input type="number" min="0" step="0.01" className="form-control" name="overall_discount_amount" value={form.overall_discount_amount} onChange={handleChange} placeholder="0.00" style={{ width: "190px" }} />
                </div>
                <h5 className="fw-bold mb-2" style={{ color: "#111827" }}>Total: ₹ {totalAmount}</h5>
              </div>
            </div>

           <div className="d-flex gap-2 mt-4">
  <button
    className="btn btn-warning"
    onClick={saveDraft}
    disabled={isSavingDraft}
  >
    {isSavingDraft ? "Saving..." : state.draftId ? "Save Changes" : "Save Draft"}
  </button>

  <button
    className="btn btn-primary flex-grow-1"
    onClick={handleSubmit}
  >
    Preview Purchase Order
  </button>
</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default POPage;
